import { useContext, useEffect, useMemo, useState } from 'react';
import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queryClient } from 'src/config/request';
import { DraggingPageContext } from 'src/modules/contentList/contexts/draggingPageContext';
import { ModuleCacheUpdater } from 'src/modules/contentList/helpers';
import { useIsExtraModule } from 'src/modules/contentList/hooks/useIsExtraModule';
import { contentListQueries } from 'src/modules/contentList/queries';
import { PageActionsService } from 'src/modules/contentList/services/pageActionsService';
import { Toast } from 'src/shared/components/toast';
export const PAGE_DRAG_HANDLE_PROPS = { 'data-draghandle': 'page' };
export const PAGE_DRAG_NODE_PROPS = { 'data-dragnode': 'page' };
export function usePageDragAndDrop({ pages, moduleId }) {
    const { t } = useTranslation();
    const isExtraModule = useIsExtraModule();
    const reorderPageMutation = useMutation({
        mutationFn: PageActionsService.reorderPage,
        mutationKey: ['reorderPage']
    });
    const { setDraggingPageId, draggingPageId } = useContext(DraggingPageContext);
    const [loaded, setLoaded] = useState(false);
    const [parentRef, pagesCopy, setPagesCopy] = useDragAndDrop(pages ?? [], {
        group: 'pages',
        dragHandle: '[data-draghandle="page"]',
        dropZone: true,
        draggable: (child) => child.dataset.dragnode === 'page',
        onDragstart: function (data) {
            setDraggingPageId(data.draggedNode.data.value.id);
        },
        onDragend: async function (data) {
            setDraggingPageId(null);
            try {
                const pageNewIndex = data.state.targetIndex;
                const pageId = data.draggedNodes[0].data.value.id;
                const targetModuleId = data.parent.el.dataset.dragparentid;
                if (!targetModuleId) {
                    console.error(`Missing attribute data-dragparentid in parent`, data.parent.el);
                    return;
                }
                const targetPage = queryClient.getQueryData(contentListQueries.getPages(targetModuleId).queryKey)?.[pageNewIndex];
                if (targetPage?.id === pageId)
                    return;
                await reorderPageMutation.mutateAsync({
                    pageId,
                    newModuleId: targetModuleId,
                    targetPageOrderId: targetPage?.id ?? null
                });
                Toast.show({ type: 'success', message: t('content_list.reorder_page.success') });
                queryClient.invalidateQueries(contentListQueries.getPages(targetModuleId));
                // If page moved to different module
                if (moduleId !== targetModuleId) {
                    queryClient.invalidateQueries(contentListQueries.getPages(moduleId));
                    // Update total pages from both modules without refetching
                    ModuleCacheUpdater.updateTotalPagesAfterPageReorder(moduleId, targetModuleId, isExtraModule);
                }
            }
            catch {
                Toast.show({ type: 'danger', message: t('content_list.reorder_page.error') });
                setPagesCopy(pages ?? []);
            }
        }
    });
    useEffect(function updateDragAndDropPagesReference() {
        setPagesCopy(pages ?? []);
        if (pages)
            setLoaded(true);
    }, [pages, setPagesCopy]);
    const parentProps = useMemo(() => ({
        ref: parentRef,
        'data-dragparentid': moduleId
    }), [parentRef, moduleId]);
    return {
        loaded,
        parentProps,
        pagesCopy,
        isDraggingPage: Boolean(draggingPageId)
    };
}
